import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { LocationDetails, OtherDetails, PersonelDetails } from '../model/create_user';
import { useFeedback } from '../../../providers/feedback/feeedback';
import { useQuery } from 'react-query';
import { SchemeMaster, Value } from '../../schemes/models/scheme_master';
import { getMasterData } from '../../../services/choices/choices_services';
import { modifyUserService } from '../../../services/user_append_service/create_modify_user_service';
import { Box, Card, CardContent, IconButton, Tab, Tabs, TextField, Typography } from '@mui/material';
import Loading from '../../../components/loading/loading';
import PersonalDetails, { subscribeToNewsLetter } from '../components/personal_details/personal_details';
import LocationDetailsComp from '../components/location_details/location_details';
import OtherDetailsComp from '../components/other_details/other_details';
import DurableDetailsComp from '../components/durable_details/durable_details';
import MainLayout from '../../../layouts/main/main_layout';
import { ArrowBack, EditRounded } from '@mui/icons-material';
import { AppUsersDetailsModel } from '../model/app_users_model';
import { getAppUsersById } from '../../../services/app_users/app_users';
import ConfirmationPopupForModifyingUser from '../components/popup_for_modifying_user/confirmation_popup';
import { queryClient } from '../../../providers/query/query';
import AreaOfInterest from '../components/area_of_interest/area_of_interest';
import { black, greyEnabled, purple, purpleLight } from '../../../theme/colors';
import { DEFAULT_ORG_ID } from '../../../constants';
import { getOrganizations } from '../../../services/organizations/organizations';
import { Organization } from '../../organizations/model/organization';
import useUser from '../../../providers/user/user_provider';

export default function ModifyUser() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [stepperCount, setStepperCount] = useState(1);
    const [uid, setUid] = useState<number | null>(null);
    const [personalDeatails, setPersonalDetails] = useState<PersonelDetails | null>(null);
    const [locationDetails, setLocationDetails] = useState<LocationDetails | null>(null);
    const [otherDetails, setOtherDetails] = useState<OtherDetails | null>(null);
    const [durableDetails, setDurableDetails] = useState<number[] | null>(null);
    const [currentTab, setCurrentTab] = React.useState(0);
    const { showSnackbar } = useFeedback();
    const [confirmationPopup, setConfirmationPopupOpen] = useState<boolean>(false);
    const [areaOfInterest, setAreaOfInterest] = useState<number[] | null>(null);
    const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const { data: appUsersDetails, isLoading } = useQuery<AppUsersDetailsModel>(
        ["appUsersDetails", id],
        async () => {
            let response = await getAppUsersById({
                id: +id!,
            });
            return response?.data?.data;
        }
    );

    // Waits on appUsersDetails so the org this user actually belongs to is
    // known before fetching - without it, the backend falls back to the
    // caller's own session org, which is wrong whenever a platform admin is
    // editing a user in a different org.
    const { data: masterDataList, isLoading: isMasterDataLoading } = useQuery<SchemeMaster>(
        ["masterDataList", appUsersDetails?.orgId],
        async () => {
            let response = await getMasterData(appUsersDetails?.orgId);
            return response.data.data || [];
        },
        { enabled: !isLoading }
    );

    // Read-only — a user's org never changes via edit, so this is purely
    // informational (matches the Edit Role screen's convention). The
    // organizations list is Platform-org-gated on the backend, same as
    // OrgSelector, so this stays disabled for everyone else.
    const { userPermissions } = useUser();
    const { data: organizationsList } = useQuery(
        ["org-selector-list"],
        async () => {
            let response = await getOrganizations({ page: 1, size: 1000 });
            return (response?.data?.data?.list || []) as Organization[];
        },
        { enabled: !!userPermissions?.isPlatformOrg }
    );
    const userOrgName = organizationsList?.find((org) => org.id === appUsersDetails?.orgId)?.name;


    function modifyUserApiCall({ sysCom }: { sysCom: string }) {
        setIsSubmitting(true);
        modifyUserService({
            address_line1: locationDetails?.houseNumber!,
            address_line2: locationDetails?.buildingName!,
            address_line3: locationDetails?.streetName!,
            area: locationDetails?.locality!,
            areas_of_interest_id: areaOfInterest!,
            district_id: locationDetails?.district?.id === 0 ? null : locationDetails?.district?.id!,
            dob: personalDeatails?.dob!,
            durable_ownership_id: durableDetails,
            education_id: otherDetails?.educational?.id === 0 ? null : otherDetails?.educational?.id!,
            family_income_id: +otherDetails?.familyIncome?.id! === 0 ? null : otherDetails?.familyIncome?.id!,
            family_type_id: otherDetails?.familyType?.id === 0 ? null : otherDetails?.familyType?.id!,
            gender_id: personalDeatails?.gender?.id ?? null,
            house_ownership_id: otherDetails?.ownershipOfHouse?.id === 0 ? null : otherDetails?.ownershipOfHouse?.id!,
            landmark: locationDetails?.landmark!,
            lat: '',
            lon: '',
            marital_status_id: personalDeatails?.meritalStatus?.id === 0 ? null : personalDeatails?.meritalStatus?.id!,
            mobile_no: personalDeatails?.mobileNumber!,
            name: personalDeatails?.fullName!,
            org_id: DEFAULT_ORG_ID,
            id_proof_type: personalDeatails?.idProofType || null,
            id_proof_number: personalDeatails?.idProofNumber || null,
            occupation_id: otherDetails?.occupational?.id === 0 ? null : otherDetails?.occupational?.id!,
            personal_income_id: otherDetails?.personalIncome?.id === 0 ? null : otherDetails?.personalIncome?.id!,
            pincode: locationDetails?.pincode!,
            preferred_language: 'en',
            state_id: locationDetails?.state!?.id === 0 ? null : locationDetails?.state!?.id,
            working_status_id: otherDetails?.workingStatus?.id === 0 ? null : otherDetails?.workingStatus?.id!,
            is_subscribed: personalDeatails?.subscribeToNewsLatter?.value === 'Yes' ? true : false,
            id: +id!,
            systemComments: sysCom
        }).then((res) => {
            if (res.status === 200) {
                showSnackbar("User details updated successfully");
                // Same key as userDetails.tsx's query (unified above) plus the
                // users list, so View Details and the list also pick up the
                // change instead of serving their cached pre-edit data for
                // the rest of the 5-minute default staleTime.
                queryClient.invalidateQueries({ queryKey: ["appUsersDetails", id] });
                queryClient.invalidateQueries({ queryKey: ["app-Users"] });
                setDisableSaveBtn(true)
                setConfirmationPopupOpen(false)
            }
        }).catch((e) => {
            showSnackbar("Something went Wrong");
        }).finally(() => {
            setIsSubmitting(false);
        });

    }
    useEffect(() => {
        let gender: Value[] = masterDataList?.gender?.filter((i) =>
            appUsersDetails?.gender === i.value
        )!
        let subscribeNewletter: { id: number, value: string, b_value: boolean }[] = subscribeToNewsLetter?.filter((i) =>
            appUsersDetails?.isSubscribed === i.b_value
        )

        let personalDetails: PersonelDetails = {
            "fullName": appUsersDetails?.name!,
            uid: 0,
            dob: appUsersDetails?.dob.toString()!,
            gender: gender?.[0]!,
            mobileNumber: +appUsersDetails?.mobileNumber!,
            idProofType: appUsersDetails?.id_proof_type ?? null,
            idProofNumber: appUsersDetails?.id_proof_number ?? '',
            familyIncome: {
                id: +appUsersDetails?.familyIncomeId!,
                value: appUsersDetails?.familyIncome!
            },
            subscribeToNewsLatter: subscribeNewletter[0]!,
            newsLatterEmailId: '',
            meritalStatus: {
                id: appUsersDetails?.maritalStatusId!,
                value: appUsersDetails?.maritalStatus!
            },
            email: appUsersDetails?.emailId

        }
        let locationDetails: LocationDetails = {
            houseNumber: appUsersDetails?.address_line1,
            buildingName: appUsersDetails?.address_line2,
            streetName: appUsersDetails?.address_line3,
            landmark: appUsersDetails?.landmark,
            locality: appUsersDetails?.area!,
            state: {
                id: +appUsersDetails?.stateId!,
                value: appUsersDetails?.state!
            },
            district: {
                id: +appUsersDetails?.districtId!,
                value: appUsersDetails?.district!
            },
            pincode: +appUsersDetails?.pincode!
        }
        let otherDetails: OtherDetails = {
            educational: {
                id: +appUsersDetails?.educationId!,
                value: appUsersDetails?.education!
            },
            workingStatus: {
                id: +appUsersDetails?.workingStatusId!,
                value: appUsersDetails?.workingStatus!
            },
            occupational: {
                id: +appUsersDetails?.occupationId!,
                value: appUsersDetails?.occupation!
            },
            familyType: {
                id: +appUsersDetails?.familyTypeId!,
                value: appUsersDetails?.familyType!
            },
            ownershipOfHouse: {
                id: +appUsersDetails?.houseOwnershipId!,
                value: appUsersDetails?.houseOwnership!
            },
            personalIncome: {
                id: +appUsersDetails?.personalIncomeId!,
                value: appUsersDetails?.personalIncome!
            },
            familyIncome: {
                id: +appUsersDetails?.familyIncomeId!,
                value: appUsersDetails?.familyIncome!
            }
        }
        let durable: number[] = [];
        let areaOfInterest: number[] = [];
        appUsersDetails?.durableOwnership?.map((v) => durable.push(+v.id!))!;
        appUsersDetails?.areasOfInterest?.map((v) => areaOfInterest.push(+v.id!))!;
        setPersonalDetails(personalDetails);
        setLocationDetails(locationDetails);
        setOtherDetails(otherDetails);
        setDurableDetails(durable!);
        setAreaOfInterest(areaOfInterest!)
        // setCurrentTab(0)
        console.log(personalDeatails);

    }, [appUsersDetails]);


    function renderCompBasedOnStepper() {
        switch (currentTab) {
            case 0:
                return <PersonalDetails
                    onSubmit={async (data) => {
                        setPersonalDetails(data);
                        setConfirmationPopupOpen(true);
                    }}
                    setUid={setUid}
                    uid={uid}
                    stepperCount={stepperCount}
                    setStepperCount={setStepperCount}
                    masterDataList={masterDataList!}
                    personalDeatails={personalDeatails!}
                    modifyUser={true}
                    disableSaveBtn={disableSaveBtn}
                    setDisableSaveBtn={setDisableSaveBtn}
                />
            case 1:
                return <LocationDetailsComp
                    onSubmit={async (data) => {
                        setLocationDetails(data);
                        setConfirmationPopupOpen(true);
                    }}
                    setUid={setUid}
                    uid={uid}
                    stepperCount={stepperCount}
                    setStepperCount={setStepperCount}
                    masterDataList={masterDataList!}
                    locationDetails={locationDetails!}
                    modifyUser={true}
                    disableSaveBtn={disableSaveBtn}
                    setDisableSaveBtn={setDisableSaveBtn}
                />
            case 2:
                return <OtherDetailsComp
                    onSubmit={async (data) => {
                        setOtherDetails(data)
                        setConfirmationPopupOpen(true);
                    }}
                    setUid={setUid}
                    uid={uid}
                    stepperCount={stepperCount}
                    setStepperCount={setStepperCount}
                    masterDataList={masterDataList!}
                    otherDetails={otherDetails!}
                    modifyUser={true}
                    disableSaveBtn={disableSaveBtn}
                    setDisableSaveBtn={setDisableSaveBtn}
                />
            case 3:
                return <DurableDetailsComp
                    stepperCount={stepperCount}
                    setStepperCount={setStepperCount}
                    masterDataList={masterDataList!}
                    masterDataLoading={isMasterDataLoading}
                    onSubmit={() => {
                        setConfirmationPopupOpen(true)
                    }}
                    durableDetails={durableDetails!}
                    setDurableDetails={setDurableDetails}
                    modifyUser={true}
                    disableSaveBtn={disableSaveBtn}
                    setDisableSaveBtn={setDisableSaveBtn}
                />
            case 4:
                return <AreaOfInterest
                    stepperCount={stepperCount}
                    setStepperCount={setStepperCount}
                    masterDataList={masterDataList!}
                    masterDataLoading={isMasterDataLoading}
                    onSubmit={() => {
                        setConfirmationPopupOpen(true)
                    }}
                    areaOfInterest={areaOfInterest!}
                    setAreaOfInterest={setAreaOfInterest}
                    modifyUser={true}
                    disableSaveBtn={disableSaveBtn}
                    setDisableSaveBtn={setDisableSaveBtn}
                />
            default:
        }
    }

    const handleChange = (event: React.SyntheticEvent, currentTabVal: number) => {
        setCurrentTab(currentTabVal);
    };


    return (
        <MainLayout>
            <Box>
                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBack />
                    </IconButton>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                bgcolor: purpleLight,
                                flexShrink: 0,
                            }}
                        >
                            <EditRounded sx={{ color: purple, fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight={700} color={black}>
                                Modify User Details
                            </Typography>
                            <Typography variant="body2" color={greyEnabled}>
                                Update the details below across any of the steps.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Card
                    elevation={0}
                    sx={{ width: '100%', mt: 2, borderRadius: 3, border: '1px solid #ECECEC' }}
                >
                    <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                        {isLoading ? (
                            <Box display="flex" justifyContent="center" py={8}>
                                <Loading />
                            </Box>
                        ) : (
                            <Box>
                                {!!userPermissions?.isPlatformOrg && (
                                    <Box mb={3} maxWidth={360}>
                                        <TextField
                                            value={userOrgName || ""}
                                            fullWidth
                                            label="Organisation"
                                            disabled
                                        />
                                    </Box>
                                )}
                                <Tabs
                                    value={currentTab}
                                    onChange={handleChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    TabIndicatorProps={{
                                        style: { backgroundColor: purple, height: 3, borderRadius: 3 },
                                    }}
                                    sx={{ borderBottom: '1px solid #ECECEC', mb: 3 }}
                                >
                                    {[
                                        "Personal Details",
                                        "Location Details",
                                        "Other Details",
                                        "Durable Details",
                                        "Area of interest",
                                    ].map((label) => (
                                        <Tab
                                            key={label}
                                            label={label}
                                            sx={{
                                                textTransform: "none",
                                                fontWeight: 600,
                                                color: greyEnabled,
                                                "&.Mui-selected": { color: purple },
                                            }}
                                        />
                                    ))}
                                </Tabs>
                                {renderCompBasedOnStepper()}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
            <ConfirmationPopupForModifyingUser
                onDialogClose={() => {
                    setConfirmationPopupOpen(false)
                }}
                open={confirmationPopup}
                updateUserAccess={(comments) => {
                    modifyUserApiCall({ sysCom: comments })
                }}
                submitting={isSubmitting}
            />
        </MainLayout>
    )

}
